import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ExportedKeyDto,
  SendInput,
  SendResultDto,
  SwapQuoteDto,
  SwapResultDto,
  TokenSearchResultDto,
  WalletActivityItemDto,
  WalletBalancesDto,
  WalletDto,
} from '@ultrasnip/contracts';
import { ApiRequestError, api } from '@/lib/api-client';

export const WalletKeys = {
  all: ['wallets'] as const,
  balances: (walletId: string) => ['wallets', walletId, 'balances'] as const,
  activity: (walletId: string) => ['wallets', walletId, 'activity'] as const,
};

export const TokenKeys = {
  popular: ['tokens', 'popular'] as const,
  search: (query: string) => ['tokens', 'search', query.toLowerCase()] as const,
};

export const walletsQuery = queryOptions({
  queryKey: WalletKeys.all,
  queryFn: ({ signal }) => api.get<WalletDto[]>('/wallets', { signal }),
  staleTime: 60_000,
});

export function balancesQuery(walletId: string) {
  return queryOptions({
    queryKey: WalletKeys.balances(walletId),
    queryFn: ({ signal }) => api.get<WalletBalancesDto>(`/wallets/${walletId}/balances`, { signal }),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
}

export function activityQuery(walletId: string) {
  return queryOptions({
    queryKey: WalletKeys.activity(walletId),
    queryFn: ({ signal }) => api.get<WalletActivityItemDto[]>(`/wallets/${walletId}/activity`, { signal }),
    staleTime: 15_000,
    refetchInterval: 45_000,
  });
}

/** Wallet list mutations all return the new list (or the changed wallet) and refresh the cache. */
export function useWalletMutations() {
  const queryClient = useQueryClient();
  const refreshList = () => queryClient.invalidateQueries({ queryKey: WalletKeys.all, exact: true });

  const create = useMutation({
    mutationFn: (label?: string) => api.post<WalletDto>('/wallets', label ? { label } : {}),
    onSuccess: refreshList,
  });
  const importKey = useMutation({
    mutationFn: (input: { secretKey: string; label?: string }) =>
      api.post<WalletDto>('/wallets/import', input),
    onSuccess: refreshList,
  });
  const rename = useMutation({
    mutationFn: ({ walletId, label }: { walletId: string; label: string }) =>
      api.patch<WalletDto>(`/wallets/${walletId}`, { label }),
    onSuccess: refreshList,
  });
  const setDefault = useMutation({
    mutationFn: (walletId: string) => api.post<WalletDto[]>(`/wallets/${walletId}/default`),
    onSuccess: (wallets) => queryClient.setQueryData(WalletKeys.all, wallets),
  });
  const archive = useMutation({
    mutationFn: (walletId: string) => api.delete<WalletDto[]>(`/wallets/${walletId}`),
    onSuccess: (wallets) => queryClient.setQueryData(WalletKeys.all, wallets),
  });
  return { create, importKey, rename, setDefault, archive };
}

export function useSendMutation(walletId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendInput) => api.post<SendResultDto>(`/wallets/${walletId}/send`, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: WalletKeys.balances(walletId) });
      void queryClient.invalidateQueries({ queryKey: WalletKeys.activity(walletId) });
    },
  });
}

export function useExportKeyMutation(walletId: string) {
  return useMutation({
    mutationFn: () => api.post<ExportedKeyDto>(`/wallets/${walletId}/export`, {}),
  });
}

/**
 * Jupiter-backed reads hit a shared rate limit that clears within seconds (keyless: 5 calls per
 * 10 s). Those 429s are retried quietly with backoff; every other error surfaces at once.
 */
const RETRY_WHEN_RATE_LIMITED = {
  retry: (failureCount: number, error: Error) =>
    error instanceof ApiRequestError && error.status === 429 && failureCount < 4,
  retryDelay: (attempt: number) => Math.min(2_000 * 2 ** attempt, 8_000),
} as const;

export const popularTokensQuery = queryOptions({
  queryKey: TokenKeys.popular,
  queryFn: ({ signal }) => api.get<TokenSearchResultDto[]>('/tokens/popular', { signal }),
  staleTime: 10 * 60_000,
  ...RETRY_WHEN_RATE_LIMITED,
});

export function tokenSearchQuery(query: string) {
  return queryOptions({
    queryKey: TokenKeys.search(query),
    queryFn: ({ signal }) =>
      api.get<TokenSearchResultDto[]>('/tokens/search', { query: { q: query }, signal }),
    staleTime: 60_000,
    enabled: query.trim().length > 0,
    placeholderData: keepPreviousData,
    ...RETRY_WHEN_RATE_LIMITED,
  });
}

export interface SwapQuoteParams {
  inputMint: string;
  outputMint: string;
  amount: string;
  slippageBps: number | null;
}

/** Live quote: refreshed before it expires; never persisted (each one holds a server-side order). */
export function swapQuoteQuery(walletId: string, params: SwapQuoteParams | null) {
  return queryOptions({
    queryKey: ['swap-quote', walletId, params] as const,
    queryFn: ({ signal }) =>
      api.get<SwapQuoteDto>(`/wallets/${walletId}/swap/quote`, {
        query: {
          inputMint: params?.inputMint ?? '',
          outputMint: params?.outputMint ?? '',
          amount: params?.amount ?? '',
          ...(params?.slippageBps ? { slippageBps: String(params.slippageBps) } : {}),
        },
        signal,
      }),
    enabled: params !== null,
    staleTime: 0,
    gcTime: 30_000,
    ...RETRY_WHEN_RATE_LIMITED,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useSwapMutation(walletId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => api.post<SwapResultDto>(`/wallets/${walletId}/swap`, { requestId }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: WalletKeys.balances(walletId) });
      void queryClient.invalidateQueries({ queryKey: WalletKeys.activity(walletId) });
    },
  });
}
